import { createModel, RematchDispatch } from '@rematch/core';
import { message } from 'antd';
import produce from 'immer';
import {
  DEFAULT_PAGINATION,
  FILE_NAME_TYPE,
  MEDIA_TYPE,
} from '../../../commons/constants/constants';
import { uploadFiles } from '../../../services/uploader.service';
import { Media, PaginationType } from '../../../types/CommonTypes';
import { storeName as mediaEntity } from '../../entities/media.entity';
import { RootModel } from '../../models';

export const storeName = 'media';

const initialState = {
  ids: [],
  pagination: DEFAULT_PAGINATION,
  editId: null,
  loading: false,
  criteria: undefined,
  visible: false,
};

export const media: any = createModel<RootModel>()({
  name: storeName,
  state: { ...initialState },
  reducers: {
    setLoading: produce((state, payload) => {
      state.loading = payload;
    }),
    setVisible: produce((state, payload) => {
      state.visible = payload;
    }),
    setEditId: produce((state, payload) => {
      state.editId = payload;
    }),
    setIds: produce((state, payload) => {
      state.ids = payload;
    }),
    setPagination: produce((state, payload) => {
      state.pagination = payload;
    }),
    setCriteria: produce((state, payload) => {
      state.criteria = payload;
    }),
    resetState: produce((state, payload) => {
      return {
        ...initialState,
      };
    }),
  },
  effects: (dispatch: RematchDispatch<typeof media>) => ({
    async saveMultipleMedia(payload, state) {
      this.setLoading(true);
      const { user } = state.user;
      try {
        const { files, type, tags, shared = false } = payload;

        let fileNameType;
        switch (type) {
          case MEDIA_TYPE.AUDIO:
            fileNameType = FILE_NAME_TYPE.AUDIO;
            break;
          case MEDIA_TYPE.VIDEO:
            fileNameType = FILE_NAME_TYPE.VIDEO;
            break;
          case MEDIA_TYPE.IMAGE:
            fileNameType = FILE_NAME_TYPE.IMAGE;
            break;
          case MEDIA_TYPE.ICON:
            fileNameType = FILE_NAME_TYPE.ICON;
            break;
          default:
            break;
        }
        const fileRest = await uploadFiles(null, files, fileNameType, user.id, 'media');
        const { filesList } = fileRest;
        const createPromises = filesList.map(({ id, url, name }) => {
          const params = {
            name,
            type,
            tags,
            shared,
            file: { id, url },
          };

          return dispatch[mediaEntity].create(params);
        });

        const res = await Promise.all(createPromises);
        message.success('Save media successfully');
        dispatch.media.loadMedia();
        return res;
      } catch (err) {
        message.error(`Fail to save media : ${err.message}`);
        console.error(err.message);
      } finally {
        this.setLoading(false);
      }
      return null;
    },
    async reloadList(_, state) {
      const { criteria, pagination } = state[storeName];
      await this.loadMedia({ ...criteria, ...pagination });
    },
    async loadMedia(payload) {
      this.setLoading(true);
      try {
        const data: { content?: Media[] } & PaginationType = await dispatch[mediaEntity].load(
          payload
        );
        const { content, ...pagination } = data;
        this.setIds(content.map((item) => item.id));
        this.setPagination(pagination);
        this.setCriteria(payload);
        return content;
      } catch (e) {
        console.error(e);
        message.error('Failed to get media list');
      } finally {
        this.setLoading(false);
      }
    },
    async pagingAsync(payload: { page?: number; size?: number }, rootState) {
      const { criteria = {} } = rootState[storeName];
      const request = {
        ...criteria,
        ...payload,
      };
      await this.loadMedia(request);
    },
    async deleteMedia(payload) {
      this.setLoading(true);
      try {
        await dispatch[mediaEntity].delete(payload);
        this.reloadList();
      } catch (e) {
        console.error(e);
        message.error('Failed to delete media');
      } finally {
        this.setLoading(false);
      }
    },
    async updateSourceFile(payload, state) {
      const { file, config } = payload;
      this.setLoading(true);
      try {
        const { user } = state.user;
        const { editId } = state[storeName];
        const med: Media = await dispatch[mediaEntity].loadById(editId);
        const { type } = med;
        let fileNameType;
        switch (type) {
          case MEDIA_TYPE.AUDIO:
            fileNameType = FILE_NAME_TYPE.AUDIO;
            break;
          case MEDIA_TYPE.VIDEO:
            fileNameType = FILE_NAME_TYPE.VIDEO;
            break;
          case MEDIA_TYPE.IMAGE:
            fileNameType = FILE_NAME_TYPE.IMAGE;
            break;
          case MEDIA_TYPE.ICON:
            fileNameType = FILE_NAME_TYPE.ICON;
            break;
          default:
            break;
        }

        const fileRest = await uploadFiles(null, [file], fileNameType, user.id, 'media');
        const { filesList } = fileRest;
        const { id, url, name } = filesList[0];
        const params = {
          ...med,
          ...config,
          name,
          file: { id, url }
        };

        await dispatch[mediaEntity].update(params);
        this.reloadList();
        this.setEditId(null);
      } catch (e) {
        console.error(e);
        message.error('Failed to update media source file');
      } finally {
        this.setLoading(false);
      }
    },
  }),
});

export default media;
